import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'umi';
import classnames from 'classnames';
import Filter from './components/Filter';
import CompareCore from '../../components/CompareCore';
import { fetchDigitalRevenueRegionsCountriesPlus } from '../../services';
import { getValidRegionsForRegionSelect, getValidRegionsForRegionSelectFromExtendsValue } from '../../utils';
import { generateMetrics } from '../../utils/dataSourcesMetrics';
import { PC_CONSOLE_COMPARE_NAMESPACE, IPcConsoleCompareState } from '@/pages/intelligence/models/pcConsoleCompare';
import { METRICS_MAPPING, PERIODS_METRICS_CONFIG, MSCIENCE_ARPPU } from '../../constants';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { IFilter, IRegion } from '@/pages/intelligence/types/pcConsoleCompare';
import type { ICompareCoreInstance } from '../../components/CompareCore/types';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';

const {
  PAGE_KEY,
  PAGE_NAME,
  ENTITY_TYPES,
  PERIODS,
  DATA_SOURCES_METRICS,
  DEVICES,
  SEGMENTS,
  DATE_TYPE,
  DATE_TYPES,
  VISUALIS_TOOLTIP_TITLE,
  PAGE_LOG_KEY,
} = PERIODS_METRICS_CONFIG.DIGITAL_REVENUE;
const METRICS_OPTIONS = generateMetrics(DATA_SOURCES_METRICS);
const METRICS_VALUES = METRICS_OPTIONS?.map((metric) => metric?.value) || [];
const INITIAL_METRICS_VALUES =
  METRICS_VALUES?.filter(
    (metric) => metric?.includes(METRICS_MAPPING.REVENUE) || metric?.includes(METRICS_MAPPING.UNITS),
  ) || [];

type IDigitalRevenueProps = IBaseProps;

export default function DigitalRevenue(props: IDigitalRevenueProps) {
  const { style, className } = props;

  const dispatch = useDispatch();

  const { filter: filterState } = useSelector<any, IPcConsoleCompareState>(
    (state: any) => state[PC_CONSOLE_COMPARE_NAMESPACE],
  );

  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.comparePCDigitalRevenue?.type,
  });
  const [isReset, setIsReset] = useState(false);

  const compareCoreInstance = useRef<ICompareCoreInstance | null>(null);

  const initialRegionData = getValidRegionsForRegionSelect(filterState?.regionData);

  /** 筛选器部分数据 **/
  const [filter, setFilter] = useState<IFilter>({
    metrics: [...INITIAL_METRICS_VALUES],
    devices: [...DEVICES],
    segments: [...SEGMENTS],
    regionData: {
      type: 'market',
      regions: [],
      markets: ['global'],
    },
    isHeatMap: false,
    isShowTop3Countries: false,
  });
  /**
   * 筛选器change的处理方法
   * 筛选器啥都没动，直接点确定，也直接重新请求对比表格、折线图、指标累计值的数据
   * 筛选器的Heat Map修改时，不需要重新请求 对比表格、折线图、指标累计值的数据，前端直接展示各个阶段、各个指标中 值最高的游戏的指标
   * 筛选器的Show Top3 Countries 勾选时，仅需要重新请求对比表格的数据，不重新请求折线图、指标累计值的数据，取消show Top3 Countries勾选时，不用重新请求接口，前端隐藏top3 Countries的UI即可
   * 筛选器中除Heat Map、Show Top3 Countries外 还有其他变化，则需要重新请求 对比表格、折线图、指标累计值的数据
   * 所以，当需要重新请求折线图数据时，肯定也需要重新请求 对比表格的数，反之重新请求对比表格数据时，不一定需要重新请求 折线图、指标累计值的数据
   * @param payload 筛选器数据
   * @param isTablesNeedReReq 是否需要重新请求对比表格的数据
   * @param isTrendsNeedReReq 是否需要重新请求折线图、指标累计值的数据（一个接口）
   */
  const handleFilterChange = (payload: IFilter, isTablesNeedReReq = true, isTrendsNeedReReq = true) => {
    if (!compareCoreInstance.current) return;
    const filterTemp = {
      ...filter,
      ...payload,
    };
    setFilter(filterTemp);
    if (isTrendsNeedReReq) {
      compareCoreInstance.current?.updateCompareTableData({ filter: filterTemp });
      compareCoreInstance.current?.updateCompareTrendsData({ filter: filterTemp });
      dispatch({
        type: `${PC_CONSOLE_COMPARE_NAMESPACE}/saveFilterRegionData`,
        payload: payload?.regionData,
      });
    } else if (isTablesNeedReReq) {
      compareCoreInstance.current?.updateCompareTableData({ filter: filterTemp });
      dispatch({
        type: `${PC_CONSOLE_COMPARE_NAMESPACE}/saveFilterRegionData`,
        payload: payload?.regionData,
      });
    }
    setIsReset(false)
  };
  /** 筛选器部分数据 **/

  /** 国家筛选器初始化部分，包含国家筛选器选项数据 **/
  const [regionsCountries, setRegionsCountries] = useState<IRegion[]>([]);
  const getRegionsCountries = async () => {
    const regionsCountriesTemp = await fetchDigitalRevenueRegionsCountriesPlus();
    return Array.isArray(regionsCountriesTemp) ? regionsCountriesTemp : [];
  };
  /** 国家筛选器初始化部分 **/

  /** 页面初始化逻辑，先查询国家筛选器数据，排除继承无效的国家数据 然后查询对比结果数据 **/
  useEffect(() => {
    (async () => {
      if (!compareCoreInstance.current) return;
      const regionsCountriesTemp = await getRegionsCountries();
      const regionDataTemp = getValidRegionsForRegionSelectFromExtendsValue(regionsCountriesTemp, initialRegionData);
      const filterTemp = {
        ...filter,
        regionData: {
          type: regionDataTemp?.type || 'market',
          regions: regionDataTemp?.regions || [],
          markets: regionDataTemp?.markets || ['global'],
        },
      };
      setFilter(filterTemp);
      // 这里国家筛选器相关的东西，要先设置国家筛选器的value，再在setTimeout内，再设置国家筛选器的选项数据，不然 首次打开国家筛选器选择面板 选项的默认选中状态有问题
      setTimeout(() => {
        setRegionsCountries(regionsCountriesTemp);
      }, 100);
      compareCoreInstance.current?.updateCompareTableData({ filter: filterTemp });
      compareCoreInstance.current?.updateCompareTrendsData({ filter: filterTemp });
    })();
  }, []);
  /** 页面初始化逻辑，先查询国家筛选器数据，排除继承无效的国家数据 然后查询对比结果数据 **/

  return (
    <div
      style={style}
      className={classnames('dbi-pc-console-compare dbi-pc-console-compare-digital-revenue', className)}
    >
      {/* 筛选器 */}
      <Filter
        metrics={METRICS_VALUES}
        metricsOptions={METRICS_OPTIONS}
        regionsCountries={regionsCountries}
        filter={filter}
        entityType={'combine'}
        onChange={handleFilterChange}
        handleReset={() => {
          setIsReset(true)
        }}
      />
      <CompareCore
        isReset={isReset}
        setIsReset={setIsReset}
        rangePickerProps={{ defaultValue: latestTimeTypeHasData }}
        ref={compareCoreInstance}
        className={classnames({
          'dbi-pc-console-compare-digital-revenue-first-column-more-width': filter?.metrics?.includes(MSCIENCE_ARPPU),
        })}
        pageKey={PAGE_KEY}
        pageName={PAGE_NAME}
        entityTypes={ENTITY_TYPES}
        filter={filter}
        periods={PERIODS}
        devices={DEVICES}
        dateType={DATE_TYPE}
        dateTypes={DATE_TYPES}
        visualisTooltipTitle={VISUALIS_TOOLTIP_TITLE}
        pageLogKey={PAGE_LOG_KEY}
        isOnlySingleMetricLine={true}
      />
    </div>
  );
}
